import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Award, Target, TrendingUp, Clock, Search, Eye, BookOpen, Video } from 'lucide-react';


import { useRealtime } from '../../contexts/RealtimeContext';
import { formatTimestamp } from '../../utils/timestampUtils';
import { calculateSectionalResultsFromStoredData } from '../../utils/sectionalAnalysis';
import AnalysisVideoModal from '../../components/AnalysisVideoModal';
import { getTestById } from '../../firebase/collections';

// Component for video analysis button that fetches video URL if needed
const VideoAnalysisButton = ({ result, testVideoCache, setTestVideoCache, onVideoClick }) => {
  // First check if result has the video URL
  const resultVideoUrl = result.analysisVideoUrl || result.percentileAnalysisVideo || result.videoAnalysisUrl;
  const cachedVideoUrl = result.testId ? testVideoCache[result.testId] : null;
  const initialVideoUrl = resultVideoUrl || cachedVideoUrl || null;
  
  const [hasVideo, setHasVideo] = React.useState(!!initialVideoUrl);
  const [videoUrl, setVideoUrl] = React.useState(initialVideoUrl);
  const [isLoading, setIsLoading] = React.useState(false);
  const [hasFetched, setHasFetched] = React.useState(!!cachedVideoUrl);

  React.useEffect(() => {
    // If we already have a video URL, no need to fetch
    if (initialVideoUrl) {
      return;
    }

    // If we've already fetched for this test, don't fetch again
    if (hasFetched || !result.testId) {
      return;
    }

    // Fetch test data
    const fetchVideoUrl = async () => {
      setIsLoading(true);
      setHasFetched(true);
      try {
        const testResult = await getTestById(result.testId);
        if (testResult.success && testResult.data) {
          const testVideoUrl = testResult.data.analysisVideoUrl;
          if (testVideoUrl && testVideoUrl.trim() !== '') {
            // Cache it
            setTestVideoCache(prev => ({ ...prev, [result.testId]: testVideoUrl }));
            setVideoUrl(testVideoUrl);
            setHasVideo(true);
          }
        }
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    };

    fetchVideoUrl();
  }, [result.testId, initialVideoUrl, hasFetched, setTestVideoCache]);

  // Check if URL is YouTube
  const isYouTubeUrl = (url) => {
    if (!url) return false;
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.includes('youtube.com') || urlObj.hostname.includes('youtu.be');
    } catch {
      return false;
    }
  };

  return (
    <button
      onClick={() => {
        if (hasVideo && videoUrl) {
          // If it's a YouTube URL, open directly in new tab
          if (isYouTubeUrl(videoUrl)) {
            window.open(videoUrl, '_blank', 'noopener,noreferrer');
          } else {
            // For other video URLs, show in modal
            onVideoClick({
              url: videoUrl,
              testName: result.testName || result.title || `Test ${result.testId?.slice(-6) || 'Unknown'}`
            });
          }
        }
      }}
      disabled={!hasVideo || isLoading}
      className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
        hasVideo
          ? 'text-purple-600 bg-purple-50 border border-purple-200 hover:bg-purple-100 dark:bg-purple-900 dark:text-purple-300 dark:border-purple-700 dark:hover:bg-purple-800'
          : 'text-gray-400 bg-gray-50 border border-gray-200 cursor-not-allowed dark:bg-gray-800 dark:text-gray-600 dark:border-gray-700'
      }`}
      title={hasVideo ? 'View Analysis by 99 Percentiler' : isLoading ? 'Checking for video...' : 'Not analysed yet'}
    >
      <Video size={16} className="mr-2" />
      {isLoading ? 'Checking...' : hasVideo ? '99% Analysis' : 'Not analysed yet'}
    </button>
  );
};

const TestResultsList = () => {
  const { userData, isConnected, connectionStatus, currentUser, isInitialized } = useRealtime();
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('date');

  const [currentPage, setCurrentPage] = useState(1);
  const [resultsPerPage] = useState(10);
  const [selectedAnalysisVideo, setSelectedAnalysisVideo] = useState(null);
  const [isAnalysisModalOpen, setIsAnalysisModalOpen] = useState(false);
  const [testVideoCache, setTestVideoCache] = useState({}); // Cache test video URLs

  // Get real test results data from Firebase
  const testResults = userData.testResults || [];
  

  


  useEffect(() => {
    if (isConnected) {
      // Add a small delay to ensure data is loaded
      const timer = setTimeout(() => {
        setLoading(false);
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [isConnected, userData.testResults]);

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-600 bg-green-100 dark:bg-green-900 dark:text-green-300';
    if (score >= 80) return 'text-blue-600 bg-blue-100 dark:bg-blue-900 dark:text-blue-300';
    if (score >= 70) return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900 dark:text-yellow-300';
    return 'text-red-600 bg-red-100 dark:bg-red-900 dark:text-red-300';
  };

  const getScoreMessage = (score) => {
    if (score >= 90) return 'Excellent!';
    if (score >= 80) return 'Great job!';
    if (score >= 70) return 'Good work!';
    if (score >= 60) return 'Not bad!';
    return 'Keep practicing!';
  };

  const filteredResults = testResults.filter(result => {
    // Exclude manual evaluation tests - they should not appear in test results list
    if (result.manualEvaluation === true) {
      return false;
    }
    
    // Check if the result has the required properties for display
    if (!result.id) {
      return false;
    }
    
    const testName = result.testName || result.title || `Test ${result.testId?.slice(-6) || 'Unknown'}`;
    const examType = result.examType || result.exam || 'Mock Test';
    const status = result.status || 'completed'; // Default to completed if no status
    
    const matchesSearch = testName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         examType.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || status === filterStatus;
    
    return matchesSearch && matchesFilter;
  });

  const sortedResults = [...filteredResults].sort((a, b) => {
    switch (sortBy) {
      case 'date':
        return new Date(b.completedAt || b.createdAt || 0) - new Date(a.completedAt || a.createdAt || 0);
      case 'score':
        return (b.percentageScore || b.score || 0) - (a.percentageScore || a.score || 0);
      case 'name':
        const nameA = a.testName || a.title || `Test ${a.testId?.slice(-6) || 'Unknown'}`;
        const nameB = b.testName || b.title || `Test ${b.testId?.slice(-6) || 'Unknown'}`;
        return nameA.localeCompare(nameB);
      default:
        return 0;
    }
  });

  // Pagination logic
  const totalPages = Math.ceil(sortedResults.length / resultsPerPage);
  const indexOfLastResult = currentPage * resultsPerPage;
  const indexOfFirstResult = indexOfLastResult - resultsPerPage;
  const currentResults = sortedResults.slice(indexOfFirstResult, indexOfLastResult);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus, sortBy]);

  const goToPage = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      // Use the formatTimestamp utility which handles Firestore timestamps properly
      return formatTimestamp(dateString, { includeTime: false, short: true });
    } catch (error) {
      // Error formatting date
      return 'Invalid Date';
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    
    // If it's a number (seconds), convert to HH:MM format
    if (typeof timeString === 'number') {
      const hours = Math.floor(timeString / 3600);
      const minutes = Math.floor((timeString % 3600) / 60);
      const seconds = timeString % 60;
      
      if (hours > 0) {
        return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
      }
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }
    
    // If it's already a formatted string, return as is
    return timeString;
  };

  // Loading state
  if (!isConnected) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Connecting to real-time data...</p>
          <p className="text-sm text-gray-500 mt-2">Status: {connectionStatus}</p>
          <p className="text-sm text-gray-500 mt-1">Current User: {currentUser?.uid || 'None'}</p>
          <p className="text-sm text-gray-500 mt-1">Initialized: {isInitialized ? 'Yes' : 'No'}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading test results...</p>
          <p className="text-sm text-gray-500 mt-2">Test Results Count: {userData?.testResults?.length || 0}</p>
        </div>
      </div>
    );
  }

  const stats = {
    totalTests: sortedResults.length, // Use filtered results for accurate count
    averageScore: sortedResults.length > 0 ? Math.round(sortedResults.reduce((sum, test) => sum + (test.percentageScore || test.score || 0), 0) / sortedResults.length) : 0,
    bestScore: sortedResults.length > 0 ? Math.max(...sortedResults.map(test => test.percentageScore || test.score || 0)) : 0,
    totalTime: sortedResults.reduce((sum, test) => {
      return sum + (test.totalTimeSpent || test.timeSpent || 0);
    }, 0)
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 px-4 sm:px-6 lg:px-8 pb-4 sm:pb-6 lg:pb-8 pt-2">
             <div className="max-w-7xl mx-auto">
         {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Test Results
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Track your performance across all mock tests and practice sessions
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700"
          >
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <Award className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Tests</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalTests}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700"
          >
            <div className="flex items-center">
              <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                <Target className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Average Score</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.averageScore}%</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700"
          >
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                <TrendingUp className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Best Score</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.bestScore}%</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700"
          >
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
                <Clock className="h-6 w-6 text-orange-600 dark:text-orange-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Time</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {Math.floor(stats.totalTime / 3600)}h {Math.floor((stats.totalTime % 3600) / 60)}m
                </p>
              </div>
            </div>
          </motion.div>
        </div>

                 {/* Filters and Search */}
         <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  placeholder="Search tests..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
            </div>
                         <div className="flex gap-2">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                <option value="all">All Status</option>
                <option value="completed">Completed</option>
                <option value="in-progress">In Progress</option>
              </select>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                <option value="date">Sort by Date</option>
                <option value="score">Sort by Score</option>
                <option value="name">Sort by Name</option>
              </select>
            </div>
          </div>
        </div>

                 {/* Results List */}
         <div className="space-y-4">
           {currentResults.map((result, index) => (
            <motion.div
              key={result.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow"
            >
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-3">
                                         <div>
                       <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                         {result.testName || result.title || `Test ${result.testId?.slice(-6) || 'Unknown'}`}
                       </h3>
                       <p className="text-sm text-gray-600 dark:text-gray-400">
                         {result.examType || result.exam || 'Mock Test'} • {formatDate(result.completedAt || result.createdAt || result.updatedAt)}
                       </p>
                     </div>
                    <div className={`px-3 py-1 rounded-full text-sm font-medium ${getScoreColor(result.percentageScore || result.score)}`}>
                      {result.percentageScore || result.score}%
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600 dark:text-gray-400">Questions</p>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {result.totalQuestions || result.questions?.length || (result.correctAnswers + result.wrongAnswers + result.unattempted) || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-600 dark:text-gray-400">Correct</p>
                      <p className="font-medium text-gray-900 dark:text-white">{result.correctAnswers || 0}</p>
                    </div>
                    <div>
                      <p className="text-gray-600 dark:text-gray-400">Accuracy</p>
                      <p className="font-medium text-gray-900 dark:text-white">{result.accuracy || 0}%</p>
                    </div>
                    <div>
                      <p className="text-gray-600 dark:text-gray-400">Time</p>
                      <p className="font-medium text-gray-900 dark:text-white">{formatTime(result.totalTimeSpent || result.timeSpent)}</p>
                    </div>
                  </div>
                  
                  {/* Sectional Results Summary */}
                  {result.sectionalResults && result.sectionalResults.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Section Performance:</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {result.sectionalResults.map((section, idx) => (
                          <div key={section.id || idx} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-gray-900 dark:text-white truncate">
                                {section.name}
                              </p>
                              <p className="text-xs text-gray-600 dark:text-gray-400">
                                {section.correctAnswers}/{section.totalQuestions}
                              </p>
                            </div>
                            <div className="ml-2">
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                section.accuracy >= 80 ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                                section.accuracy >= 60 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                                'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                              }`}>
                                {section.accuracy}%
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="flex items-center gap-2 mt-4 lg:mt-0 lg:ml-6 flex-wrap">
                  <Link
                    to={`/dashboard/test-results/${encodeURIComponent(result.id)}`}
                    className="inline-flex items-center px-3 py-2 text-sm font-medium text-primary-600 bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100 dark:bg-primary-900 dark:text-primary-300 dark:border-primary-700 dark:hover:bg-primary-800 transition-colors"
                  >
                    <Eye size={16} className="mr-2" />
                    View Details
                  </Link>
                  <VideoAnalysisButton
                    result={result}
                    testVideoCache={testVideoCache}
                    setTestVideoCache={setTestVideoCache}
                    onVideoClick={(videoData) => {
                      setSelectedAnalysisVideo(videoData);
                      setIsAnalysisModalOpen(true);
                    }}
                  />
                </div>
              </div>
            </motion.div>
          ))}
                 </div>

         {/* Pagination Controls */}
         {totalPages > 1 && (
           <div className="flex items-center justify-between mt-8">
             <div className="text-sm text-gray-700 dark:text-gray-300">
               Showing {indexOfFirstResult + 1} to {Math.min(indexOfLastResult, sortedResults.length)} of {sortedResults.length} results
             </div>
             <div className="flex items-center space-x-2">
               <button
                 onClick={goToPreviousPage}
                 disabled={currentPage === 1}
                 className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                   currentPage === 1
                     ? 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-800 dark:text-gray-600'
                     : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700'
                 }`}
               >
                 Previous
               </button>
               
               {/* Page Numbers */}
               <div className="flex items-center space-x-1">
                 {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                   let pageNumber;
                   if (totalPages <= 5) {
                     pageNumber = i + 1;
                   } else if (currentPage <= 3) {
                     pageNumber = i + 1;
                   } else if (currentPage >= totalPages - 2) {
                     pageNumber = totalPages - 4 + i;
                   } else {
                     pageNumber = currentPage - 2 + i;
                   }
                   
                   return (
                     <button
                       key={pageNumber}
                       onClick={() => goToPage(pageNumber)}
                       className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                         currentPage === pageNumber
                           ? 'bg-blue-600 text-white'
                           : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700'
                       }`}
                     >
                       {pageNumber}
                     </button>
                   );
                 })}
                 
                 {totalPages > 5 && currentPage < totalPages - 2 && (
                   <span className="px-2 text-gray-500">...</span>
                 )}
                 
                 {totalPages > 5 && currentPage < totalPages - 2 && (
                   <button
                     onClick={() => goToPage(totalPages)}
                     className="px-3 py-2 text-sm font-medium bg-white text-gray-700 hover:bg-gray-50 border border-gray-300 rounded-lg dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
                   >
                     {totalPages}
                   </button>
                 )}
               </div>
               
               <button
                 onClick={goToNextPage}
                 disabled={currentPage === totalPages}
                 className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                   currentPage === totalPages
                     ? 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-800 dark:text-gray-600'
                     : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700'
                 }`}
               >
                 Next
               </button>
             </div>
           </div>
         )}

         {sortedResults.length === 0 && (
          <div className="text-center py-12">
            <BookOpen className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No test results found</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {searchTerm ? 'Try adjusting your search terms.' : 'Start taking some mock tests to see your results here.'}
            </p>
            {!searchTerm && (
              <div className="mt-6">
                <Link
                  to="/dashboard/mock-tests"
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Take a Mock Test
                </Link>
              </div>
            )}
            
          </div>
        )}
      </div>

      {/* Analysis Video Modal */}
      <AnalysisVideoModal
        isOpen={isAnalysisModalOpen}
        onClose={() => {
          setIsAnalysisModalOpen(false);
          setSelectedAnalysisVideo(null);
        }}
        videoUrl={selectedAnalysisVideo?.url}
        testName={selectedAnalysisVideo?.testName}
      />
    </div>
  );
};

export default TestResultsList;
